/*
 * why do we need a service : This service is used to manage the breadcrumb state and provide a way to navigate through the breadcrumb items.
 * It allows for dynamic updates to the breadcrumb items based on the current route
 * and provides methods to navigate to breadcrumb items.
 * This service is useful for maintaining a consistent breadcrumb experience across different components and routes in the application.
 */
import type { Signal } from '@angular/core';
import { computed, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { filter, map } from 'rxjs';

import type { BreadcrumbItem } from '../../types/breadcrumb-item.type';

export type BreadcrumbRouteData = {
  breadcrumb?: string;
  breadcrumbPath?: string;
};

export type BreadcrumbConfig = {
  baseKey: string;
  translateFn: (key: string) => string;
  langChange?: Signal<unknown>;
  lang?: () => string;
};

export const BREADCRUMB_CONFIG = new InjectionToken<BreadcrumbConfig>('BreadcrumbConfig');

const IMS_HOME_BASE_URL = 'https://ims.evidentscientific.com';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute, { optional: true });
  readonly #config = inject(BREADCRUMB_CONFIG, { optional: true });

  readonly #dynamicNode = signal<BreadcrumbItem | undefined>(undefined);
  readonly #navigationEnd = this.#config
    ? toSignal(
        this.#router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          map((event) => event.urlAfterRedirects),
        ),
      )
    : signal<string | undefined>(undefined);

  readonly items = computed<BreadcrumbItem[]>(() => {
    this.#navigationEnd();
    const config = this.#config;
    if (!config) return [];

    config.langChange?.();
    const base: BreadcrumbItem = { id: '', name: config.translateFn(config.baseKey), link: '/' };
    const segments = this.#buildSegments().map((segment) => ({
      id: segment.path,
      name: config.translateFn(segment.labelKey),
      link: `/${segment.path}`,
    }));
    const node = this.#dynamicNode();

    return node ? [base, ...segments, node] : [base, ...segments];
  });

  setDynamicNode(item: BreadcrumbItem): void {
    this.#dynamicNode.set(item);
  }

  clearDynamicNode(): void {
    this.#dynamicNode.set(undefined);
  }

  getPathForItem(item: BreadcrumbItem): string {
    return `/${item.id}`;
  }

  navigateToItem(item: BreadcrumbItem): void {
    this.#router.navigate([this.getPathForItem(item)]);
  }

  navigateToImsHome(): void {
    const lang = this.#config?.lang?.() ?? '';
    window.location.href = `${IMS_HOME_BASE_URL}/${lang}/`;
  }

  #buildSegments(): { path: string; labelKey: string }[] {
    const segments: { path: string; labelKey: string }[] = [];
    let route: ActivatedRoute | null = this.#route?.root ?? null;

    while (route) {
      const children: ActivatedRoute[] = route.children;
      route = null;

      for (const child of children) {
        const data = child.snapshot.data as BreadcrumbRouteData;
        if (data.breadcrumb) {
          segments.push({
            path:
              data.breadcrumbPath ?? child.snapshot.url.map((s: UrlSegment) => s.path).join('/'),
            labelKey: data.breadcrumb,
          });
        }
        route = child;
      }
    }

    return segments;
  }
}
